import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideMarkdown } from 'ngx-markdown';
import { firstValueFrom, switchMap } from 'rxjs';
import { routes } from './app.routes';
import { errorInterceptor, jwtInterceptor } from './core/interceptors';
import { ChunkErrorHandler, clearChunkReloadFlag } from './core/services';
import { AuthenticationService } from './core/authentication/authentication.service';
import { TenantConfigService } from './core/tenant/tenant-config.service';

const SKIP_SESSION_RESTORE_ROUTES = ['reset-password'];

function shouldRestoreSession(document: Document): boolean {
  const pathSegments = document.location.pathname
    .toLowerCase()
    .split('/')
    .filter(Boolean);

  return !SKIP_SESSION_RESTORE_ROUTES.some((route) => pathSegments.includes(route));
}

/**
 * Initializes the application on startup.
 * Loads tenant configuration first, then attempts to restore the user session.
 * This runs before the app is fully bootstrapped.
 *
 * Order of operations:
 * 1. Load tenant config (sets document title, favicon, theme)
 * 2. Initialize authentication session (restore user from refresh token)
 */
function initializeApp(): Promise<boolean> {
  const document = inject(DOCUMENT);
  const tenantService = inject(TenantConfigService);
  const authService = inject(AuthenticationService);

  // Clear the chunk-reload guard so a future deployment can trigger a reload
  clearChunkReloadFlag();

  return firstValueFrom(
    tenantService.loadConfig().pipe(
      switchMap(() =>
        shouldRestoreSession(document)
          ? authService.initializeSession()
          : authService.initializeAnonymousSession(),
      ),
    ),
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    provideRouter(routes),
    provideHttpClient(withFetch(), withInterceptors([jwtInterceptor, errorInterceptor])),
    provideMarkdown(),
    provideAppInitializer(() => initializeApp()),
  ],
};
